import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCurrency } from '../../utils/formatters';
import { getTaxCategoryInfo } from '../../constants/taxCategories';
import TaxCategorySelector from '../common/TaxCategorySelector';
import CurrencyInput from 'react-currency-input-field';
import { useLanguage } from '../../contexts/LanguageContext';

interface TaxSubtotal {
    TaxableAmount: number;
    TaxAmount: number;
    TaxCategory: {
        ID: string;
        Percent: number;
        TaxScheme: {
            ID: string;
        };
    };
}

interface TaxSubtotalsDisplayProps {
    subtotals: TaxSubtotal[];
    readOnly?: boolean;
    onChange?: (subtotals: TaxSubtotal[]) => void;
    onAddSubtotal?: () => void;
    onDeleteSubtotal?: (index: number) => void;
    currency?: string;
    totalTaxAmount?: number;
    showValidation?: boolean;
}

const getSeparatorsByLanguage = (language: string) => {
    return language.startsWith('de') 
        ? { decimalSeparator: ',', groupSeparator: '.' }
        : { decimalSeparator: '.', groupSeparator: ',' };
};

export const TaxSubtotalsDisplay: React.FC<TaxSubtotalsDisplayProps> = ({
    subtotals,
    readOnly = true,
    onChange,
    onAddSubtotal,
    onDeleteSubtotal,
    currency = 'EUR',
    totalTaxAmount = 0,
    showValidation = false
}) => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const separators = getSeparatorsByLanguage(language);
    const [expandedRows, setExpandedRows] = useState<number[]>([]);

    // Tax categories that require exemption reason
    const EXEMPTION_REQUIRED_CATEGORIES = ['E', 'AE', 'K', 'G', 'O'];

    // Helper function to parse number from string with locale consideration
    const parseLocaleNumber = (value: string | number): number => {
        if (typeof value === 'number') return value;
        if (!value) return 0;
        
        // If using German format (comma as decimal separator), replace comma with dot
        if (separators.decimalSeparator === ',') {
            // First remove thousand separators (dots in German format)
            const cleanValue = value.toString().replace(/\./g, '').replace(',', '.');
            return parseFloat(cleanValue) || 0;
        }
        
        // For English format, remove thousand separators (commas)
        const cleanValue = value.toString().replace(/,/g, '');
        return parseFloat(cleanValue) || 0;
    };

    const handleSubtotalChange = (index: number, field: keyof TaxSubtotal | 'TaxCategory.ID' | 'TaxCategory.Percent', value: any) => {
        if (!onChange) return;
        
        const newSubtotals = [...subtotals];
        if (field === 'TaxCategory.ID') {
            newSubtotals[index].TaxCategory.ID = value;
        } else if (field === 'TaxCategory.Percent') {
            const numericValue = parseLocaleNumber(value);
            newSubtotals[index].TaxCategory.Percent = numericValue;
            // Recalculate tax amount when percent changes
            newSubtotals[index].TaxAmount = Math.round((newSubtotals[index].TaxableAmount * numericValue) / 100 * 100) / 100;
        } else if (field === 'TaxableAmount') {
            const numericValue = parseLocaleNumber(value);
            newSubtotals[index].TaxableAmount = numericValue;
            // Recalculate tax amount when taxable amount changes
            newSubtotals[index].TaxAmount = Math.round((numericValue * newSubtotals[index].TaxCategory.Percent) / 100 * 100) / 100;
        } else if (field === 'TaxAmount') {
            newSubtotals[index].TaxAmount = parseLocaleNumber(value);
        }
        
        onChange(newSubtotals);
    };

    const toggleRow = (index: number) => {
        setExpandedRows(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const renderLabelWithInfo = (labelKey: string, descriptionKey: string) => (
        <div className="flex items-center gap-2">
            <span className="field-label">{t(labelKey)}</span>
            <div className="relative group">
                <svg 
                    className="w-4 h-4 text-text-muted hover:text-text-primary cursor-help transition-colors"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M7.5 0.875C3.84375 0.875 0.875 3.84375 0.875 7.5C0.875 11.1562 3.84375 14.125 7.5 14.125C11.1562 14.125 14.125 11.1562 14.125 7.5C14.125 3.84375 11.1562 0.875 7.5 0.875ZM7.5 3.5C7.91562 3.5 8.25 3.83437 8.25 4.25C8.25 4.66563 7.91562 5 7.5 5C7.08437 5 6.75 4.66563 6.75 4.25C6.75 3.83437 7.08437 3.5 7.5 3.5ZM9 11H6C5.72375 11 5.5 10.7762 5.5 10.5C5.5 10.2238 5.72375 10 6 10H6.75V7H6.5C6.22375 7 6 6.77625 6 6.5C6 6.22375 6.22375 6 6.5 6H7.5C7.77625 6 8 6.22375 8 6.5V10H8.75C9.02625 10 9.25 10.2238 9.25 10.5C9.25 10.7762 9.02625 11 8.75 11H9Z"
                        fill="currentColor"
                    />
                </svg>
                <div className="absolute left-full top-0 ml-2 px-4 py-2 bg-bg-secondary text-text-primary text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-md min-w-[200px] max-w-[300px] z-10">
                    {t(descriptionKey)}
                </div>
            </div>
        </div>
    );

    if (!subtotals || subtotals.length === 0) {
        return (
            <div className="text-sm text-text-secondary italic">
                {t('invoiceFields.taxSubtotals.noSubtotals')}
            </div>
        );
    }

    // Calculate totals
    const totalTaxableAmount = subtotals.reduce((sum, s) => sum + s.TaxableAmount, 0);
    const calculatedTotalTaxAmount = subtotals.reduce((sum, s) => sum + s.TaxAmount, 0);

    // Show validation warning if tax amounts don't match
    const hasValidationError = showValidation && Math.abs(calculatedTotalTaxAmount - totalTaxAmount) > 0.01;

    return (
        <div>
            {/* Validation Error */}
            {hasValidationError && (
                <div className="mb-4 p-3 bg-red-50 border border-error rounded-md">
                    <div className="flex items-center gap-2 text-error">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">
                            {t('invoiceFields.taxSubtotals.validationError', {
                                calculated: formatCurrency(calculatedTotalTaxAmount, currency),
                                expected: formatCurrency(totalTaxAmount, currency)
                            })}
                        </span>
                    </div>
                </div>
            )}

            <div>
                {/* Header */}
                <div className="grid grid-cols-4 gap-4 px-4 py-3 md-label-large text-text-primary border-b border-border-secondary bg-bg-tertiary rounded-t-lg shadow-sm">
                    <div className="truncate font-medium">{t('invoiceFields.taxSubtotals.category')}</div>
                    <div className="text-right font-medium">{t('invoiceFields.taxSubtotals.rate')}</div>
                    <div className="text-right font-medium">{t('invoiceFields.taxSubtotals.taxableAmount')}</div>
                    <div className="text-right font-medium">{t('invoiceFields.taxSubtotals.taxAmount')}</div>
                </div>
                
                {/* Tax subtotal lines */}
                <div className="space-y-3 mt-3">
                    {subtotals.map((subtotal, index) => {
                        const categoryInfo = getTaxCategoryInfo(subtotal.TaxCategory.ID);
                        return (
                            <div key={index} className="md-card overflow-hidden md-duration-medium">
                                {/* Main Row */}
                                <div
                                    className={`grid grid-cols-4 gap-4 px-4 py-3 cursor-pointer md-standard-easing
                                        hover:bg-hover-bg active:bg-active-bg transition-colors rounded-md
                                        ${expandedRows.includes(index) ? 'bg-focus-bg elevation-1 border-l-2 border-brand-primary' : 'hover:elevation-1'}`}
                                    onClick={() => toggleRow(index)}
                                >
                                    <div className="flex items-center gap-2">
                                        <svg
                                            className={`w-4 h-4 transition-transform duration-200 text-text-muted flex-shrink-0
                                                ${expandedRows.includes(index) ? 'transform rotate-90' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="m9 18 6-6-6-6"
                                            />
                                        </svg>
                                        <span className="font-medium truncate">
                                            {subtotal.TaxCategory.ID}
                                            {categoryInfo && (
                                                <span className="text-sm text-text-secondary ml-2">
                                                    ({language === 'de' ? categoryInfo.germanName : categoryInfo.name})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="text-right">{subtotal.TaxCategory.Percent}%</div>
                                    <div className="text-right">{formatCurrency(subtotal.TaxableAmount, currency)}</div>
                                    <div className="text-right font-medium">{formatCurrency(subtotal.TaxAmount, currency)}</div>
                                </div>

                                {/* Expanded Details */}
                                {expandedRows.includes(index) && (
                                    <div className="md-card-content border-t border-border-secondary bg-bg-secondary/40 space-y-2 overflow-x-hidden md-scrollbar elevation-1 rounded-b-md">
                                        {/* Tax Category */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLabelWithInfo(
                                                'invoiceFields.taxSubtotals.category',
                                                'invoiceFields.taxSubtotals.categoryDescription'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {subtotal.TaxCategory.ID}
                                                    {categoryInfo && (
                                                        <span className="text-sm text-text-secondary ml-2">
                                                            ({language === 'de' ? categoryInfo.germanName : categoryInfo.name})
                                                        </span>
                                                    )}
                                                </span>
                                            ) : (
                                                <TaxCategorySelector
                                                    value={subtotal.TaxCategory.ID}
                                                    onChange={(value) => handleSubtotalChange(index, 'TaxCategory.ID', value)}
                                                    placeholder={t('invoiceFields.taxSubtotals.selectCategory')}
                                                    disabled={readOnly}
                                                    showGermanNames={language === 'de'}
                                                    className="w-full"
                                                />
                                            )}
                                        </div>

                                        {/* Tax Rate */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLabelWithInfo(
                                                'invoiceFields.taxSubtotals.rate',
                                                'invoiceFields.taxSubtotals.rateDescription'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {subtotal.TaxCategory.Percent}%
                                                </span>
                                            ) : (
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        value={subtotal.TaxCategory.Percent}
                                                        onChange={(e) => handleSubtotalChange(index, 'TaxCategory.Percent', e.target.value)}
                                                        className="w-full px-3 py-2 pr-8 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">%</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Taxable Amount */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLabelWithInfo(
                                                'invoiceFields.taxSubtotals.taxableAmount',
                                                'invoiceFields.taxSubtotals.taxableAmountDescription'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {formatCurrency(subtotal.TaxableAmount, currency)}
                                                </span>
                                            ) : (
                                                <CurrencyInput
                                                    value={subtotal.TaxableAmount}
                                                    onValueChange={(value) => handleSubtotalChange(index, 'TaxableAmount', value)}
                                                    className="px-3 py-2 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                    decimalsLimit={2}
                                                    decimalSeparator={separators.decimalSeparator}
                                                    groupSeparator={separators.groupSeparator}
                                                    disabled={readOnly}
                                                    intlConfig={{ locale: language, currency }}
                                                    allowNegativeValue={false}
                                                />
                                            )}
                                        </div>

                                        {/* Tax Amount */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLabelWithInfo(
                                                'invoiceFields.taxSubtotals.taxAmount',
                                                'invoiceFields.taxSubtotals.taxAmountDescription'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {formatCurrency(subtotal.TaxAmount, currency)}
                                                </span>
                                            ) : (
                                                <CurrencyInput
                                                    value={subtotal.TaxAmount}
                                                    onValueChange={(value) => handleSubtotalChange(index, 'TaxAmount', value)}
                                                    className="px-3 py-2 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                    decimalsLimit={2}
                                                    decimalSeparator={separators.decimalSeparator}
                                                    groupSeparator={separators.groupSeparator}
                                                    disabled={readOnly}
                                                    intlConfig={{ locale: language, currency }}
                                                    allowNegativeValue={false}
                                                />
                                            )}
                                        </div>

                                        {/* Show tax exemption info if applicable */}
                                        {EXEMPTION_REQUIRED_CATEGORIES.includes(subtotal.TaxCategory.ID) && (
                                            <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm text-warning dark:text-warning">
                                                {t('invoiceFields.taxSubtotals.exemptionRequired')}
                                            </div>
                                        )}

                                        {/* Delete Button */}
                                        {!readOnly && onDeleteSubtotal && (
                                            <div className="flex justify-end mt-4">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDeleteSubtotal(index);
                                                    }}
                                                    className="flex items-center gap-2 text-[var(--error)] elevation-1 hover:elevation-2 active:elevation-3 bg-[var(--error)]/10 border border-[var(--error)] rounded-md px-3 py-1.5"
                                                    title={t('invoiceFields.taxSubtotals.deleteSubtotal')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    {t('invoiceFields.taxSubtotals.deleteSubtotal') || 'Delete Tax Subtotal'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Add Tax Subtotal Button */}
                {!readOnly && onAddSubtotal && (
                    <div className="mt-4">
                        <button
                            onClick={onAddSubtotal}
                            className="flex items-center gap-2 elevation-1 hover:elevation-2 active:elevation-3 bg-[var(--brand-primary)] text-[var(--text-button)] px-4 py-2 rounded-md shadow-sm"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('invoiceFields.taxSubtotals.addSubtotal') || 'Add Tax Subtotal'}
                        </button>
                    </div>
                )}

                {/* Totals Summary */}
                <div className="border-t border-border-primary pt-4 mt-4">
                    <div className="grid grid-cols-4 gap-4">
                        <div className="col-span-2"></div>
                        <div className="text-right">
                            <div className="text-sm text-text-secondary">
                                {t('invoiceFields.taxSubtotals.totalTaxableAmount')}
                            </div>
                            <div className="font-semibold text-text-primary mt-1">
                                {formatCurrency(totalTaxableAmount, currency)}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-text-secondary">
                                {t('invoiceFields.taxSubtotals.totalTaxAmount')}
                            </div>
                            <div className={`font-semibold mt-1 ${hasValidationError ? 'text-error' : 'text-text-primary'}`}>
                                {formatCurrency(calculatedTotalTaxAmount, currency)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
